import { useEffect, useState } from "react";
import { WarehouseTag } from "./WarehouseSelect";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { SampleQtyBadge } from "../pages/SamplesPage";

// iter 267: confirm shipping samples for a quotation. Delta-based: only the
// difference vs. what was already shipped is deducted / returned.
export function SampleShipDialog({ open, onOpenChange, cartId, onDone }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !cartId) return;
    setData(null); setError("");
    api.get(`/carts/${cartId}/samples/preview`).then((r) => setData(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")));
  }, [open, cartId, t]);

  const rows = data?.rows || [];
  const pending = rows.filter((r) => r.delta !== 0);
  const short = pending.filter((r) => r.delta > 0 && r.sample_qty - r.delta < 0);

  const ship = async () => {
    if (short.length && !window.confirm(t("samples.negative_confirm", { count: short.length }))) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/carts/${cartId}/samples/ship`);
      toast.success(t("samples.shipped", { count: res.applied }));
      onDone && onDone(); onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="sample-ship-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2">{t("samples.ship_title")}{data?.warehouse && <WarehouseTag name={data.warehouse.name} testId="sample-ship-warehouse" />}</DialogTitle></DialogHeader>
        {error && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="sample-ship-error">{error}</p>}
        {data?.samples_shipped_at && <p className="text-xs text-gray-500" data-testid="sample-ship-last">{t("samples.last_shipped", { time: new Date(data.samples_shipped_at).toLocaleString(), user: data.samples_shipped_by || "" })}</p>}
        <div className="border border-gray-200 max-h-80 overflow-y-auto">
          <table className="w-full text-xs" data-testid="sample-ship-table">
            <thead className="bg-gray-50"><tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-label text-gray-500">{t("samples.product")}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-label text-gray-500">{t("samples.requested")}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-label text-gray-500">{t("samples.already_shipped")}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-label text-gray-500">{t("samples.to_deduct")}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-label text-gray-500">{t("samples.stock")}</th>
            </tr></thead>
            <tbody>
              {!data && !error && <tr><td colSpan={5} className="text-center py-6 text-gray-400">…</td></tr>}
              {data && rows.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-gray-400">{t("samples.no_sample_lines")}</td></tr>}
              {rows.map((r) => {
                const insufficient = r.delta > 0 && r.sample_qty - r.delta < 0;
                return (
                  <tr key={r.product_id} className={`border-t border-gray-100 ${insufficient ? "bg-red-50/60" : ""}`} data-testid={`sample-ship-row-${r.product_id}`}>
                    <td className="px-3 py-2"><span className="font-mono">{r.item_no}</span> <span className="text-gray-400">{r.description}</span></td>
                    <td className="px-3 py-2 text-right font-mono">{r.requested}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-500">{r.shipped}</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${r.delta > 0 ? "text-red-700" : r.delta < 0 ? "text-emerald-700" : "text-gray-400"}`}>{r.delta > 0 ? `-${r.delta}` : r.delta < 0 ? `+${-r.delta}` : "0"}</td>
                    <td className="px-3 py-2 text-right"><SampleQtyBadge qty={r.sample_qty} />{insufficient && <span className="ml-1 text-[10px] text-red-600">{t("samples.insufficient")}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button onClick={ship} disabled={busy || !!error || pending.length === 0} data-testid="sample-ship-confirm">{t("samples.confirm_ship", { count: pending.length })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
